
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Vertrag {
  id: string;
  titel: string;
  vertragspartner: string;
  kategorie: string;
  startdatum: string;
  enddatum?: string;
  kuendigungsfrist: string;
  zahlungsintervall: string;
  zahlungstag: number;
  grundbetrag: number;
  umsatzbeteiligung?: number;
  mindestbetrag?: number;
  maxbetrag?: number;
  zahlungsrichtung: 'eingehend' | 'ausgehend';
  automat_id?: string;
  standort?: string;
  vertragsbedingungen: string;
  status: 'aktiv' | 'gekuendigt' | 'abgelaufen' | 'entwurf';
  notizen: string;
  file_name?: string;
  file_url?: string;
  file_size?: number;
  created_at: string;
  kontodaten?: {
    iban: string;
    bankname: string;
    adresse: string;
    bic: string;
    kontoinhaber: string;
  };
}

interface Kontakt {
  id: string;
  name: string;
  firma?: string;
}

interface Automat {
  id: string;
  name: string;
  location: string;
}

const kategorien = [
  'Mietvertrag',
  'Standplatzvertrag',
  'Dienstleistungsvertrag',
  'Liefervertrag',
  'Wartungsvertrag',
  'Versicherungsvertrag',
  'Partnerschaftsvertrag',
  'Lizenzvertrag',
  'Sonstiges'
];

const zahlungsintervalle = [
  { value: 'monatlich', label: 'Monatlich' },
  { value: 'quartalsweise', label: 'Quartalsweise' },
  { value: 'halbjaehrlich', label: 'Halbjährlich' },
  { value: 'jaehrlich', label: 'Jährlich' },
  { value: 'einmalig', label: 'Einmalig' }
];

const statusOptionen = [
  { value: 'aktiv', label: 'Aktiv' },
  { value: 'entwurf', label: 'Entwurf' },
  { value: 'gekuendigt', label: 'Gekündigt' },
  { value: 'abgelaufen', label: 'Abgelaufen' }
];

export default function Vertraege() {
  const { user } = useAuth();
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVertrag, setEditingVertrag] = useState<Vertrag | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [ansichtstyp, setAnsichtstyp] = useState<'karten' | 'tabelle'>('karten');

  // Neue States für Vertragsdetails und Kontodaten
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showKontodatenModal, setShowKontodatenModal] = useState(false);
  const [selectedVertrag, setSelectedVertrag] = useState<Vertrag | null>(null);
  const [kontodaten, setKontodaten] = useState({
    iban: '',
    bankname: '',
    adresse: '',
    bic: '',
    kontoinhaber: ''
  });

  // Formular State - das hat gefehlt!
  const [formData, setFormData] = useState({
    titel: '',
    vertragspartner: '',
    kategorie: '',
    startdatum: new Date().toISOString().split('T')[0],
    enddatum: '',
    kuendigungsfrist: '',
    zahlungsintervall: 'monatlich',
    zahlungstag: '1',
    grundbetrag: '',
    umsatzbeteiligung: '',
    mindestbetrag: '',
    maxbetrag: '',
    zahlungsrichtung: 'ausgehend' as 'eingehend' | 'ausgehend',
    automat_id: '',
    standort: '',
    vertragsbedingungen: '',
    status: 'entwurf' as 'aktiv' | 'gekuendigt' | 'abgelaufen' | 'entwurf',
    notizen: '',
    file: null as File | null,
    // Kontodaten direkt im Formular
    kontodaten: {
      iban: '',
      bankname: '',
      adresse: '',
      bic: '',
      kontoinhaber: ''
    }
  });

  // Datei-Upload zu Supabase Storage "vertrag" Bucket
  const uploadFile = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('vertrag')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('vertrag')
        .getPublicUrl(fileName);

      return { url: publicUrl };
    } catch (error) {
      console.error('Upload error:', error);
      return { url: '', error };
    }
  };

  // Datei herunterladen
  const downloadFile = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  // Daten laden
  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Verträge laden
      const { data: vertraegeData, error: vertraegeError } = await supabase
        .from('vertraege')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (vertraegeError) throw vertraegeError;

      setVertraege(vertraegeData || []);

      // Kontakte laden
      const { data: kontakteData, error: kontakteError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (kontakteError) throw kontakteError;

      const kontakteListe = kontakteData.map(item => ({
        id: item.id,
        name: item.data.name,
        firma: item.data.firma
      }));
      setKontakte(kontakteListe);

      // Automaten laden
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat')
        .order('created_at', { ascending: false });

      if (automatenError) throw automatenError;

      const automatenListe = automatenData
        .filter(item => !item.data?.type || item.data?.type !== 'reference')
        .map(item => ({
          id: item.id,
          name: item.data.name,
          location: item.data.location
        }));
      setAutomaten(automatenListe);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formular-Handler
  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
  };

  const resetForm = () => {
    setFormData({
      titel: '',
      vertragspartner: '',
      kategorie: '',
      startdatum: new Date().toISOString().split('T')[0],
      enddatum: '',
      kuendigungsfrist: '',
      zahlungsintervall: 'monatlich',
      zahlungstag: '1',
      grundbetrag: '',
      umsatzbeteiligung: '',
      mindestbetrag: '',
      maxbetrag: '',
      zahlungsrichtung: 'ausgehend',
      automat_id: '',
      standort: '',
      vertragsbedingungen: '',
      status: 'entwurf',
      notizen: '',
      file: null,
      kontodaten: {
        iban: '',
        bankname: '',
        adresse: '',
        bic: '',
        kontoinhaber: ''
      }
    });
    setEditingVertrag(null);
  };

  // Vertrag hinzufügen/bearbeiten
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    
    // Validierung
    if (!formData.titel.trim()) {
      alert('Bitte geben Sie einen Vertragstitel ein');
      return;
    }
    
    if (!formData.vertragspartner.trim()) {
      alert('Bitte wählen Sie einen Vertragspartner aus');
      return;
    }
    
    if (!formData.kategorie) {
      alert('Bitte wählen Sie eine Kategorie aus');
      return;
    }

    try {
      setUploading(true);
      console.log('Starting upload process...');
      
      const selectedAutomat = automaten.find(a => a.id === formData.automat_id);
      
      let fileUrl = null;
      let fileName = null;
      let fileSize = null;

      // Datei hochladen falls vorhanden
      if (formData.file) {
        console.log('Uploading file:', formData.file.name, 'Size:', formData.file.size);
        const { url, error } = await uploadFile(formData.file);
        if (error) {
          console.error('Error uploading file:', error);
          alert('Fehler beim Hochladen der Datei: ' + error.message);
          return;
        }
        fileUrl = url;
        fileName = formData.file.name;
        fileSize = formData.file.size;
        console.log('File uploaded successfully:', fileUrl);
      }
      
      // Daten für Datenbank vorbereiten
      const vertragData = {
        user_id: user?.id,
        titel: formData.titel.trim(),
        vertragspartner: formData.vertragspartner.trim(),
        kategorie: formData.kategorie,
        startdatum: formData.startdatum,
        enddatum: formData.enddatum || null,
        kuendigungsfrist: formData.kuendigungsfrist.trim(),
        zahlungsintervall: formData.zahlungsintervall,
        zahlungstag: formData.zahlungstag ? parseInt(formData.zahlungstag) : 1,
        grundbetrag: formData.grundbetrag ? parseFloat(formData.grundbetrag) : 0,
        umsatzbeteiligung: formData.umsatzbeteiligung ? parseFloat(formData.umsatzbeteiligung) : null,
        mindestbetrag: formData.mindestbetrag ? parseFloat(formData.mindestbetrag) : null,
        maxbetrag: formData.maxbetrag ? parseFloat(formData.maxbetrag) : null,
        zahlungsrichtung: formData.zahlungsrichtung,
        automat_id: formData.automat_id || null,
        standort: selectedAutomat?.location || formData.standort,
        vertragsbedingungen: formData.vertragsbedingungen.trim(),
        status: formData.status,
        notizen: formData.notizen.trim(),
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize,
        // Kontodaten direkt mit speichern
        kontodaten: formData.kontodaten
      };

      console.log('Saving contract data to database:', vertragData);

      if (editingVertrag) {
        // Vertrag bearbeiten
        console.log('Updating existing contract:', editingVertrag.id);
        const { data, error } = await supabase
          .from('vertraege')
          .update(vertragData)
          .eq('id', editingVertrag.id)
          .eq('user_id', user?.id)
          .select()
          .single();

        if (error) {
          console.error('Database update error:', error);
          throw error;
        }

        console.log('Contract updated successfully:', data);
        setVertraege(prev => prev.map(vertrag => 
          vertrag.id === editingVertrag.id ? data : vertrag
        ));
        alert('Vertrag erfolgreich aktualisiert!');
      } else {
        // Neuen Vertrag hinzufügen
        console.log('Creating new contract...');
        const { data, error } = await supabase
          .from('vertraege')
          .insert(vertragData)
          .select()
          .single();

        if (error) {
          console.error('Database insert error:', error);
          throw error;
        }

        console.log('Contract created successfully:', data);
        setVertraege(prev => [data, ...prev]);
        alert('Vertrag erfolgreich hinzugefügt!');
      }

      // Form zurücksetzen und Modal schließen
      resetForm();
      setIsModalOpen(false);

    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Fehler beim Speichern des Vertrags: ' + (error as any)?.message || 'Unbekannter Fehler');
    } finally {
      setUploading(false);
    }
  };

  // Vertrag bearbeiten
  const handleEdit = (vertrag: Vertrag) => {
    setEditingVertrag(vertrag);
    setFormData({
      titel: vertrag.titel,
      vertragspartner: vertrag.vertragspartner,
      kategorie: vertrag.kategorie,
      startdatum: vertrag.startdatum,
      enddatum: vertrag.enddatum || '',
      kuendigungsfrist: vertrag.kuendigungsfrist,
      zahlungsintervall: vertrag.zahlungsintervall,
      zahlungstag: vertrag.zahlungstag.toString(),
      grundbetrag: vertrag.grundbetrag.toString(),
      umsatzbeteiligung: vertrag.umsatzbeteiligung?.toString() || '',
      mindestbetrag: vertrag.mindestbetrag?.toString() || '',
      maxbetrag: vertrag.maxbetrag?.toString() || '',
      zahlungsrichtung: vertrag.zahlungsrichtung,
      automat_id: vertrag.automat_id || '',
      standort: vertrag.standort || '',
      vertragsbedingungen: vertrag.vertragsbedingungen,
      status: vertrag.status,
      notizen: vertrag.notizen,
      file: null,
      // Bestehende Kontodaten laden
      kontodaten: vertrag.kontodaten || {
        iban: '',
        bankname: '',
        adresse: '',
        bic: '',
        kontoinhaber: ''
      }
    });
    setIsModalOpen(true);
  };

  // Vertrag löschen
  const handleDelete = async (id: string) => {
    if (!confirm('Vertrag wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('vertraege')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;

      setVertraege(prev => prev.filter(vertrag => vertrag.id !== id));
    } catch (error) {
      console.error('Error deleting contract:', error);
    }
  };

  // Gefilterte Verträge
  const gefilterteVertraege = vertraege.filter(vertrag => {
    const matchesSearch = vertrag.titel.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vertrag.vertragspartner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vertrag.standort?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesKategorie = !filterKategorie || vertrag.kategorie === filterKategorie;
    const matchesStatus = !filterStatus || vertrag.status === filterStatus;
    return matchesSearch && matchesKategorie && matchesStatus;
  });

  // Status-Farbe
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aktiv':
        return 'bg-green-100 text-green-800';
      case 'entwurf':
        return 'bg-gray-100 text-gray-800';
      case 'gekuendigt':
        return 'bg-orange-100 text-orange-800';
      case 'abgelaufen':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Zahlungsrichtung-Icon
  const getZahlungsrichtungIcon = (richtung: string) => {
    return richtung === 'eingehend' ? 'ri-arrow-down-circle-line text-green-600' : 'ri-arrow-up-circle-line text-red-600';
  };

  // Kontakt-Optionen
  const kontaktOptionen = kontakte.map(kontakt => ({
    value: kontakt.name,
    label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
  }));

  const kategorieOptionen = kategorien.map(kat => ({
    value: kat,
    label: kat
  }));

  const automatOptionen = [
    { value: '', label: 'Kein Automat zugeordnet' },
    ...automaten.map(automat => ({
      value: automat.id,
      label: `${automat.name} - ${automat.location}`
    }))
  ];

  // Vertragsdetails anzeigen
  const showVertragDetails = (vertrag: Vertrag) => {
    setSelectedVertrag(vertrag);
    setShowDetailsModal(true);
  };

  // Kontodaten anzeigen/bearbeiten
  const showKontodaten = (vertrag: Vertrag) => {
    setSelectedVertrag(vertrag);
    
    // Bestehende Kontodaten laden falls vorhanden
    if (vertrag.kontodaten) {
      setKontodaten(vertrag.kontodaten);
    } else {
      setKontodaten({
        iban: '',
        bankname: '',
        adresse: '',
        bic: '',
        kontoinhaber: ''
      });
    }
    setShowKontodatenModal(true);
  };

  // Kontodaten speichern
  const saveKontodaten = async () => {
    if (!selectedVertrag) return;

    try {
      const { error } = await supabase
        .from('vertraege')
        .update({ kontodaten })
        .eq('id', selectedVertrag.id)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Lokalen State aktualisieren
      setVertraege(prev => prev.map(vertrag => 
        vertrag.id === selectedVertrag.id 
          ? { ...vertrag, kontodaten }
          : vertrag
      ));

      setShowKontodatenModal(false);
      alert('Kontodaten erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving kontodaten:', error);
      alert('Fehler beim Speichern der Kontodaten');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Verträge werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          {/* Header */}
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-file-text-line mr-3 text-blue-600"></i>
                  Verträge
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Vertrag
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Verwalten Sie Ihre Verträge mit Datei-Upload ({vertraege.length} Verträge)
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-7"
                >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Vertrag hinzufügen
              </button>
            </div>
          </div>

          {/* Statistiken */}
          {vertraege.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {vertraege.filter(v => v.status === 'aktiv').length}
                  </div>
                  <div className="text-sm text-gray-600">Aktive Verträge</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    CHF {vertraege
                      .filter(v => v.status === 'aktiv' && v.zahlungsrichtung === 'eingehend')
                      .reduce((sum, v) => sum + v.grundbetrag, 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Einnahmen</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    CHF {vertraege
                      .filter(v => v.status === 'aktiv' && v.zahlungsrichtung === 'ausgehend')
                      .reduce((sum, v) => sum + v.grundbetrag, 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Ausgaben</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {vertraege.filter(v => v.umsatzbeteiligung && v.umsatzbeteiligung > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">Umsatzbeteiligung</div>
                </div>
              </div>
            </div>
          )}

          {/* Filter */}
          {vertraege.length > 0 && (
            <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Verträge durchsuchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-5"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Select
                    options={[
                      { value: '', label: 'Alle Kategorien' },
                      ...kategorieOptionen
                    ]}
                    value={filterKategorie}
                    onChange={(value) => setFilterKategorie(value)}
                  />
                  <Select
                    options={[
                      { value: '', label: 'Alle Status' },
                      ...statusOptionen
                    ]}
                    value={filterStatus}
                    onChange={(value) => setFilterStatus(value)}
                  />
                </div>
              </div>
              
              {/* Ansichtsumschalter */}
              <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
                <button
                  onClick={() => setAnsichtstyp('karten')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    ansichtstyp === 'karten' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-layout-grid-line mr-1"></i>
                  Karten
                </button>
                <button
                  onClick={() => setAnsichtstyp('tabelle')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    ansichtstyp === 'tabelle' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-list-check mr-1"></i>
                  Tabelle
                </button>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {vertraege.length === 0 ? (
              // Empty State
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-file-text-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Verträge vorhanden
                </h3>
                <p className="text-gray-500 mb-6">
                  Fügen Sie Ihren ersten Vertrag hinzu, um zu beginnen.
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(true);
                  }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-7"
                >
                  <i className="ri-add-line mr-2"></i>
                  Ersten Vertrag hinzufügen
                </button>
              </div>
            ) : ansichtstyp === 'karten' ? (
              // Karten-Ansicht
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {gefilterteVertraege.map((vertrag) => (
                  <div key={vertrag.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <i className={getZahlungsrichtungIcon(vertrag.zahlungsrichtung)}></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{vertrag.titel}</h3>
                          <p className="text-sm text-gray-600">{vertrag.vertragspartner}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(vertrag)}
                          className="text-gray-400 hover:text-blue-600 cursor-pointer"
                          title="Bearbeiten"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(vertrag.id)}
                          className="text-gray-400 hover:text-red-600 cursor-pointer"
                          title="Löschen"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>

                    {/* Informationen */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vertrag.status)}`}>
                          {statusOptionen.find(s => s.value === vertrag.status)?.label}
                        </span>
                        <span className="text-sm text-gray-600">{vertrag.kategorie}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Grundbetrag:</span>
                          <div className={`font-semibold ${vertrag.zahlungsrichtung === 'eingehend' ? 'text-green-600' : 'text-red-600'}`}>
                            CHF {vertrag.grundbetrag.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Intervall:</span>
                          <div className="font-semibold">{zahlungsintervalle.find(z => z.value === vertrag.zahlungsintervall)?.label}</div>
                        </div>
                      </div>

                      {vertrag.umsatzbeteiligung && (
                        <div className="bg-orange-50 p-3 rounded-md">
                          <div className="flex items-center">
                            <i className="ri-line-chart-line text-orange-600 mr-2"></i>
                            <span className="text-sm text-orange-800">
                              Umsatzbeteiligung: <strong>{vertrag.umsatzbeteiligung}%</strong>
                            </span>
                          </div>
                          {(vertrag.mindestbetrag || vertrag.maxbetrag) && (
                            <div className="text-xs text-orange-700 mt-1">
                              {vertrag.mindestbetrag && `Min: CHF ${vertrag.mindestbetrag}`}
                              {vertrag.mindestbetrag && vertrag.maxbetrag && ' • '}
                              {vertrag.maxbetrag && `Max: CHF ${vertrag.maxbetrag}`}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Laufzeit:</span>
                          <span>
                            {new Date(vertrag.startdatum).toLocaleDateString('de-DE')}
                            {vertrag.enddatum && ` - ${new Date(vertrag.enddatum).toLocaleDateString('de-DE')}`}
                          </span>
                        </div>
                        {vertrag.kuendigungsfrist && (
                          <div className="flex justify-between mt-1">
                            <span className="text-gray-600">Kündigung:</span>
                            <span>{vertrag.kuendigungsfrist}</span>
                          </div>
                        )}
                      </div>

                      {vertrag.standort && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-map-pin-line mr-2"></i>
                          <span>{vertrag.standort}</span>
                        </div>
                      )}

                      {/* Datei-Info */}
                      {vertrag.file_name && vertrag.file_url && (
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                          <div className="flex items-center">
                            <i className="ri-file-text-line text-blue-600 mr-2"></i>
                            <span className="text-sm text-gray-700 truncate">{vertrag.file_name}</span>
                          </div>
                          <button
                            onClick={() => downloadFile(vertrag.file_url!, vertrag.file_name!)}
                            className="text-blue-600 hover:text-blue-700 cursor-pointer"
                            title="Datei herunterladen"
                          >
                            <i className="ri-download-line"></i>
                          </button>
                        </div>
                      )}

                      {/* Neue Action Buttons */}
                      <div className="flex space-x-2 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => showVertragDetails(vertrag)}
                          className="flex-1 bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          <i className="ri-file-list-line mr-1"></i>
                          Vertragsdetails
                        </button>
                        <button
                          onClick={() => showKontodaten(vertrag)}
                          className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          <i className="ri-bank-line mr-1"></i>
                          Kontodaten
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Tabellen-Ansicht
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vertrag</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intervall</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datei</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {gefilterteVertraege.map((vertrag) => (
                        <tr key={vertrag.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <i className={getZahlungsrichtungIcon(vertrag.zahlungsrichtung)} title={vertrag.zahlungsrichtung === 'eingehend' ? 'Eingehend' : 'Ausgehend'}></i>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{vertrag.titel}</div>
                                <div className="text-sm text-gray-500">{vertrag.kategorie}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{vertrag.vertragspartner}</div>
                            {vertrag.standort && (
                              <div className="text-sm text-gray-500">{vertrag.standort}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${vertrag.zahlungsrichtung === 'eingehend' ? 'text-green-600' : 'text-red-600'}`}>
                              CHF {vertrag.grundbetrag.toLocaleString()}
                            </div>
                            {vertrag.umsatzbeteiligung && (
                              <div className="text-sm text-orange-600">+ {vertrag.umsatzbeteiligung}% Umsatz</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {zahlungsintervalle.find(z => z.value === vertrag.zahlungsintervall)?.label}
                            </div>
                            <div className="text-sm text-gray-500">am {vertrag.zahlungstag}.</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vertrag.status)}`}>
                              {statusOptionen.find(s => s.value === vertrag.status)?.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {vertrag.file_name && vertrag.file_url ? (
                              <button
                                onClick={() => downloadFile(vertrag.file_url!, vertrag.file_name!)}
                                className="flex items-center text-blue-600 hover:text-blue-7 cursor-pointer"
                                title="Datei herunterladen"
                              >
                                <i className="ri-file-text-line mr-1"></i>
                                <span className="text-sm truncate max-w-24">{vertrag.file_name}</span>
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400">Keine Datei</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEdit(vertrag)}
                                className="text-gray-400 hover:text-blue-600 cursor-pointer"
                                title="Bearbeiten"
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(vertrag.id)}
                                className="text-gray-400 hover:text-red-600 cursor-pointer"
                                title="Löschen"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für Vertrag hinzufügen/bearbeiten */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingVertrag ? 'Vertrag bearbeiten' : 'Neuen Vertrag hinzufügen'}
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Grunddaten */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Grunddaten</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Vertragstitel"
                placeholder="z.B. Mietvertrag Standort A"
                value={formData.titel}
                onChange={(e) => handleInputChange('titel', e.target.value)}
                required
              />
              <Select
                label="Vertragspartner"
                options={kontakte.map(kontakt => ({
                  value: kontakt.name,
                  label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
                }))}
                value={formData.vertragspartner}
                onChange={(value) => handleInputChange('vertragspartner', value)}
                placeholder="Partner auswählen..."
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Kategorie"
                options={kategorien.map(kat => ({ value: kat, label: kat }))}
                value={formData.kategorie}
                onChange={(value) => handleInputChange('kategorie', value)}
                placeholder="Kategorie auswählen..."
                required
              />
              <Select
                label="Status"
                options={statusOptionen}
                value={formData.status}
                onChange={(value) => handleInputChange('status', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Startdatum"
                type="date"
                value={formData.startdatum}
                onChange={(e) => handleInputChange('startdatum', e.target.value)}
                required
              />
              <Input
                label="Enddatum (optional)"
                type="date"
                value={formData.enddatum}
                onChange={(e) => handleInputChange('enddatum', e.target.value)}
              />
            </div>
          </div>

          {/* Zahlungsbedingungen */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Zahlungsbedingungen</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zahlungsrichtung
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="zahlungsrichtung"
                      value="eingehend"
                      checked={formData.zahlungsrichtung === 'eingehend'}
                      onChange={(e) => handleInputChange('zahlungsrichtung', e.target.value)}
                      className="mr-2"
                    />
                    <i className="ri-arrow-down-circle-line text-green-600 mr-1"></i>
                    Eingehend
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="zahlungsrichtung"
                      value="ausgehend"
                      checked={formData.zahlungsrichtung === 'ausgehend'}
                      onChange={(e) => handleInputChange('zahlungsrichtung', e.target.value)}
                      className="mr-2"
                    />
                    <i className="ri-arrow-up-circle-line text-red-600 mr-1"></i>
                    Ausgehend
                  </label>
                </div>
              </div>
              <Input
                label="Grundbetrag (CHF)"
                type="number"
                step="0.01"
                value={formData.grundbetrag}
                onChange={(e) => handleInputChange('grundbetrag', e.target.value)}
                placeholder="0.00"
                required
              />
              <Select
                label="Zahlungsintervall"
                options={zahlungsintervalle}
                value={formData.zahlungsintervall}
                onChange={(value) => handleInputChange('zahlungsintervall', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Zahlungstag im Monat"
                type="number"
                min="1"
                max="31"
                value={formData.zahlungstag}
                onChange={(e) => handleInputChange('zahlungstag', e.target.value)}
              />
              <Input
                label="Kündigungsfrist"
                placeholder="z.B. 3 Monate zum Monatsende"
                value={formData.kuendigungsfrist}
                onChange={(e) => handleInputChange('kuendigungsfrist', e.target.value)}
              />
            </div>
          </div>

          {/* Umsatzbeteiligung */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Umsatzbeteiligung (optional)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Umsatzbeteiligung (%)"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.umsatzbeteiligung}
                onChange={(e) => handleInputChange('umsatzbeteiligung', e.target.value)}
                placeholder="z.B. 13"
              />
              <Input
                label="Mindestbetrag (CHF)"
                type="number"
                step="0.01"
                value={formData.mindestbetrag}
                onChange={(e) => handleInputChange('mindestbetrag', e.target.value)}
                placeholder="0.00"
              />
              <Input
                label="Höchstbetrag (CHF)"
                type="number"
                step="0.01"
                value={formData.maxbetrag}
                onChange={(e) => handleInputChange('maxbetrag', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Kontodaten */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Kontodaten</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Kontoinhaber"
                placeholder="Name des Kontoinhabers"
                value={formData.kontodaten.kontoinhaber}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kontodaten: { ...prev.kontodaten, kontoinhaber: e.target.value } 
                }))}
              />
              <Input
                label="IBAN"
                placeholder="z.B. CH93 0076 2011 6238 5295 7"
                value={formData.kontodaten.iban}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kontodaten: { ...prev.kontodaten, iban: e.target.value } 
                }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="BIC/SWIFT (optional)"
                placeholder="z.B. UBSWCHZH80A"
                value={formData.kontodaten.bic}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kontodaten: { ...prev.kontodaten, bic: e.target.value } 
                }))}
              />
              <Input
                label="Bankname"
                placeholder="z.B. UBS Switzerland AG"
                value={formData.kontodaten.bankname}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kontodaten: { ...prev.kontodaten, bankname: e.target.value } 
                }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bankadresse
              </label>
              <textarea
                value={formData.kontodaten.adresse}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  kontodaten: { ...prev.kontodaten, adresse: e.target.value } 
                }))}
                placeholder="Vollständige Adresse der Bank..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-5 focus:border-blue-5"
              />
            </div>
          </div>

          {/* Standort & Zusatzinformationen */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Standort & Zusatzinformationen</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Automat (optional)"
                options={[
                  { value: '', label: 'Kein Automat zugeordnet' },
                  ...automaten.map(automat => ({
                    value: automat.id,
                    label: `${automat.name} - ${automat.location}`
                  }))
                ]}
                value={formData.automat_id}
                onChange={(value) => handleInputChange('automat_id', value)}
              />
              <Input
                label="Standort"
                placeholder="Adresse oder Standortbeschreibung"
                value={formData.standort}
                onChange={(e) => handleInputChange('standort', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertragsdokument (optional)
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-5 focus:border-blue-5"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
              />
              <p className="mt-1 text-xs text-gray-500">
                PDF, Word, Excel oder Bild-Dateien (max. 10MB)
              </p>
              {editingVertrag?.file_name && (
                <p className="mt-1 text-xs text-blue-600">
                  Aktuelle Datei: {editingVertrag.file_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertragsbedingungen
              </label>
              <textarea
                value={formData.vertragsbedingungen}
                onChange={(e) => handleInputChange('vertragsbedingungen', e.target.value)}
                placeholder="Wichtige Vertragsbedingungen, Klauseln oder Vereinbarungen..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-5 focus:border-blue-5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notizen
              </label>
              <textarea
                value={formData.notizen}
                onChange={(e) => handleInputChange('notizen', e.target.value)}
                placeholder="Zusätzliche Notizen zum Vertrag..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-5 focus:border-blue-5"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              disabled={uploading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <i className="ri-loader-line animate-spin mr-2"></i>
                  {editingVertrag ? 'Speichere...' : 'Füge hinzu...'}
                </>
              ) : (
                editingVertrag ? 'Änderungen speichern' : 'Vertrag hinzufügen'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Vertragsdetails Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Vertragsdetails"
        maxWidth="lg"
      >
        {selectedVertrag && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4 pb-4 border-b">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className={getZahlungsrichtungIcon(selectedVertrag.zahlungsrichtung)}></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedVertrag.titel}</h3>
                <p className="text-gray-600">{selectedVertrag.vertragspartner}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Grundinformationen</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedVertrag.status)}`}>
                      {statusOptionen.find(s => s.value === selectedVertrag.status)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kategorie:</span>
                    <span>{selectedVertrag.kategorie}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Startdatum:</span>
                    <span>{new Date(selectedVertrag.startdatum).toLocaleDateString('de-DE')}</span>
                  </div>
                  {selectedVertrag.enddatum && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Enddatum:</span>
                      <span>{new Date(selectedVertrag.enddatum).toLocaleDateString('de-DE')}</span>
                    </div>
                  )}
                  {selectedVertrag.kuendigungsfrist && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kündigungsfrist:</span>
                      <span>{selectedVertrag.kuendigungsfrist}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Zahlungsdetails</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grundbetrag:</span>
                    <span className={`font-medium ${selectedVertrag.zahlungsrichtung === 'eingehend' ? 'text-green-600' : 'text-red-600'}`}>
                      CHF {selectedVertrag.grundbetrag.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Intervall:</span>
                    <span>{zahlungsintervalle.find(z => z.value === selectedVertrag.zahlungsintervall)?.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Zahlungstag:</span>
                    <span>{selectedVertrag.zahlungstag}. des Monats</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Richtung:</span>
                    <span className="flex items-center">
                      <i className={getZahlungsrichtungIcon(selectedVertrag.zahlungsrichtung)} ></i>
                      <span className="ml-1 capitalize">{selectedVertrag.zahlungsrichtung}</span>
                    </span>
                  </div>
                  {selectedVertrag.umsatzbeteiligung && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Umsatzbeteiligung:</span>
                      <span className="text-orange-600 font-medium">{selectedVertrag.umsatzbeteiligung}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedVertrag.standort && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Standort</h4>
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span>{selectedVertrag.standort}</span>
                </div>
              </div>
            )}

            {selectedVertrag.vertragsbedingungen && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Vertragsbedingungen</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedVertrag.vertragsbedingungen}</p>
                </div>
              </div>
            )}

            {selectedVertrag.notizen && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Notizen</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedVertrag.notizen}</p>
                </div>
              </div>
            )}

            {selectedVertrag.file_name && selectedVertrag.file_url && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Anhang</h4>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-file-text-line text-blue-600 mr-2"></i>
                    <span className="text-sm text-gray-700">{selectedVertrag.file_name}</span>
                  </div>
                  <button
                    onClick={() => downloadFile(selectedVertrag.file_url!, selectedVertrag.file_name!)}
                    className="text-blue-600 hover:text-blue-700 cursor-pointer"
                  >
                    <i className="ri-download-line"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Kontodaten Modal */}
      <Modal
        isOpen={showKontodatenModal}
        onClose={() => setShowKontodatenModal(false)}
        title="Kontodaten verwalten"
        maxWidth="md"
      >
        {selectedVertrag && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3 pb-4 border-b">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-bank-line text-green-600"></i>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{selectedVertrag.titel}</h3>
                <p className="text-sm text-gray-600">{selectedVertrag.vertragspartner}</p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Kontoinhaber"
                placeholder="Name des Kontoinhabers"
                value={kontodaten.kontoinhaber}
                onChange={(e) => setKontodaten(prev => ({ ...prev, kontoinhaber: e.target.value }))}
              />

              <Input
                label="IBAN"
                placeholder="z.B. CH93 0076 2011 6238 5295 7"
                value={kontodaten.iban}
                onChange={(e) => setKontodaten(prev => ({ ...prev, iban: e.target.value }))}
              />

              <Input
                label="BIC/SWIFT (optional)"
                placeholder="z.B. UBSWCHZH80A"
                value={kontodaten.bic}
                onChange={(e) => setKontodaten(prev => ({ ...prev, bic: e.target.value }))}
              />

              <Input
                label="Bankname"
                placeholder="z.B. UBS Switzerland AG"
                value={kontodaten.bankname}
                onChange={(e) => setKontodaten(prev => ({ ...prev, bankname: e.target.value }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bankadresse
                </label>
                <textarea
                  value={kontodaten.adresse}
                  onChange={(e) => setKontodaten(prev => ({ ...prev, adresse: e.target.value }))}
                  placeholder="Vollständige Adresse der Bank..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowKontodatenModal(false)}
              >
                Abbrechen
              </Button>
              <Button
                onClick={saveKontodaten}
              >
                <i className="ri-save-line mr-2"></i>
                Kontodaten speichern
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
